import React, { useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { refreshActiveTable } from '@/store/reducers/table-slice';
import { getUi, hideModal } from '@/store/reducers/ui-slice';
import { MethodResponse } from '@alien-platform/cms-bo';
import { IUserPermissions } from '@ap-db-worker/common';
import { Button, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { CustomConfigInputProps, InputType } from '@/types/general';
import { CustomConfigInputV2 } from '@/components/core/custom-config-input-v2';

function BlockUserModalContent(): React.JSX.Element {
  const { modal } = useAppSelector(getUi);

  const [dto, setDto] = useState({
    password: '',
    confirmPassword: '',
  });

  const content = modal?.content as {
    opId: string;
    userId: string;
    nickname: string;
    isBlocked: boolean;
  };

  const inputs: CustomConfigInputProps[] = [
    {
      name: 'password',
      label: 'New Password',
      required: true,
      type: InputType.INPUT,
      value: dto?.password,
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password',
      required: true,
      type: InputType.INPUT,
      value: dto?.confirmPassword,
    },
  ];

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const handleClose = (): void => {
    dispatch(hideModal());
  };

  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await fetch(`/api/users/block`, {
        method: 'PUT',
        body: JSON.stringify({
          userId: content?.userId,
          action: content?.isBlocked ? 'UNBLOCK' : 'BLOCK',
          adminId: content?.opId,
        }),
      });
      const result: MethodResponse<IUserPermissions> = await response.json();
      if (response.ok && result.success) {
        toast.success('User blocked successfully!');
        dispatch(hideModal());
        dispatch(refreshActiveTable());
      } else {
        toast.error(result.messages?.[0] || 'Error: 500 | Unable to block user');
      }
    } catch (e) {
      toast.error('Error: 500 | Unable to block user');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack>
      <Stack spacing={3}>
        <Typography variant="body2">
          Are you sure you want to block this user <b>{content?.nickname}</b>?
        </Typography>
      </Stack>
      <Stack direction="row" spacing={3} sx={{ mt: 3, justifyContent: 'initial' }}>
        <Button type="button" color="secondary" onClick={handleClose} sx={{ flex: '1 1 auto' }}>
          Close
        </Button>

        <Button loading={isLoading} onClick={handleSubmit} color="error" sx={{ flex: '1 1 auto' }} variant="contained">
          {content?.isBlocked ? 'Unblock User' : 'Block User'}
        </Button>
      </Stack>
    </Stack>
  );
}

export default BlockUserModalContent;
